import React, { memo, useRef } from 'react';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import { useAppSelector } from 'hooks';
import { useIntegrityBlockGuard } from 'hooks/useIntegrityBlockGuard';
import { useInViewport } from 'hooks/useInViewport';
import { selectMySpotFor } from 'reducers/waitlist.selectors';

import MediaImage from 'components/SaloonLobby/MediaImage';
import { PENDING_SHUTDOWN_LABEL } from 'components/SaloonLobby/shutdownCopy';
import { CashGame } from 'components/SaloonLobby/types';
import PayoutPreload from 'components/TournamentPayouts/PayoutPreload';

import { formatBuyIn } from './formatBuyIn';
import RestrictedOverlay from './RestrictedOverlay';
import { shallowEqualRecord } from './shallowEqualRecord';

import styles from './GameTile.module.scss';

interface CashGameTileProps {
  game: CashGame;
  onClick?: (id: string) => void;
  // gameIDs the player currently has open (seated OR observing) — drives the
  // .active orangy-red glow. When the player has this table open, the tile
  // gets the halo from the design spec.
  playerGameIds?: Set<string>;
  // gameIDs the player is actually SEATED at (a strict subset of the open
  // tables above). The Join Waitlist banner is suppressed only for tables the
  // player sits at — an observer of a full table still needs the banner to
  // grab a seat, so observing alone must NOT hide it.
  seatedGameIds?: Set<string>;
  // Cash tiles opt into the waitlist treatment (full-table Join Waitlist
  // overlay + joined-state stamp). Sit & Go tiles render through this same
  // component but have no waitlist, so the lobby leaves this off for them.
  waitlistEnabled?: boolean;
  // SnG tournament name/type (e.g. "SNG 3P FIFO"), shown as a small header tag
  // above the title. Cash tiles omit it.
  tournamentName?: string;
  // EN-10064: the viewer can't seat/register at this table/SnG (a flagged
  // opponent is present). When set, the dimmed lock overlay shows AND the tap is
  // blocked (the integrity modal opens instead of opening the table); the engine
  // gate remains the authoritative fallback.
  restricted?: boolean;
  // EN-10064: this component renders both cash tables and SnGs. The scope only
  // picks the block modal's title ("…at this table." vs "…at this tournament.").
  restrictedScope?: 'cash' | 'tournament';
  // Tile density (lobby Card Size preference). 'compact' shrinks the tile
  // height/banner/fonts so more games fit per screen; 'large' is the original
  // roomy tile. Defaults to 'large' so existing callers/tests are unaffected —
  // the lobby passes the user's persisted choice (compact by default).
  size?: 'compact' | 'large';
}

const SeatDots: React.FC<{ seated: number; capacity: number }> = ({ seated, capacity }) => {
  const total = Math.max(capacity, seated);
  const dots = Array.from({ length: total }, (_, i) => i < seated);
  return (
    <div className={styles.dots} aria-label={`${seated} of ${capacity} seated`}>
      {dots.map((filled, i) => (
        <span key={i} className={`${styles.dot} ${filled ? styles.dotFilled : ''}`} />
      ))}
    </div>
  );
};

const CashGameTile: React.FC<CashGameTileProps> = ({
  game,
  onClick,
  playerGameIds,
  seatedGameIds,
  waitlistEnabled = false,
  tournamentName,
  restricted = false,
  restrictedScope = 'cash',
  size = 'large'
}) => {
  const isCompact = size === 'compact';
  const { reduceAnimations } = useAnimationPreferences();
  const compactClass = isCompact ? styles.compact : '';
  // Active (orangy-red glow) = the table has live action. That's true when the
  // player has it open (seated or observing) OR when ANY player is seated there
  // — a populated table is highlighted by default in the lobby, matching Unity /
  // the design reference. Empty tables (0 seated) stay un-glowed.
  const playerHasOpen = playerGameIds?.has(game.id) ?? false;
  const active = playerHasOpen || game.seated > 0;
  // Seated = the player actually holds a seat here. Used to gate the Join
  // Waitlist banner: a full table the player is only OBSERVING must still
  // offer the banner (they left/never joined the waitlist and want a seat).
  const seatedHere = seatedGameIds?.has(game.id) ?? false;
  // Shutdown is announced to everyone immediately, but only a player who is
  // already seated may reopen the table to finish the current hand.
  const shutdownBlocked = Boolean(game.pendingShutdown && !seatedHere);
  // Pause this tile's looping animations while it's scrolled off-screen. The
  // ref lives on the unclipped .tileWrap so it works for both the plain-button
  // tile and the waitlist <div> variant below.
  const tileRef = useRef<HTMLDivElement>(null);
  const inView = useInViewport(tileRef);
  // Per-variant body tint mirrors the Skullcrusher design's m-tcard-plo /
  // m-tcard-hilo info-block treatment. NLH keeps the default parchment.
  const variantClass =
    game.variant === 'PLO' ? styles.cashPlo : game.variant === 'PLO-HiLo' ? styles.cashHilo : '';

  const blockIntegrity = useIntegrityBlockGuard();

  // EN-10064: a restricted tile is blocked at the tap — open the integrity
  // modal instead of the table. The engine gate stays the fallback.
  const handleTileClick = () => {
    if (shutdownBlocked) return;
    if (restricted) {
      blockIntegrity(game.id, restrictedScope);
      return;
    }
    onClick?.(game.id);
  };
  // Engine waitlist entries are keyed by the numeric game id; CashGame.id is
  // the same id carried as a string (adaptCashGame: String(raw.gameID)).
  const waitlistGameId = Number(game.id);
  const mySpot = useAppSelector(selectMySpotFor(waitlistGameId));
  const joined = waitlistEnabled && Boolean(mySpot);

  const isFull = game.seated >= game.capacity;
  const waitingCount = game.waitlistCount ?? 0;
  // The Join Waitlist treatment shows on a full table (or one that already
  // has a queue) when the player is neither seated here nor already queued.
  // Note: gated on `seatedHere`, NOT `active` — merely observing the table
  // (subscribed but not seated) must not suppress the banner. A restricted
  // table never shows the tile-level waitlist overlay — the tap opens the
  // integrity modal, which offers the waitlist recovery from there instead.
  const showWaitlist =
    waitlistEnabled &&
    !joined &&
    !seatedHere &&
    !restricted &&
    !game.pendingShutdown &&
    (isFull || waitingCount > 0);

  const banner = (
    <div className={styles.banner}>
      <MediaImage
        path={`images/lobby/${game.banner}`}
        alt={game.variant}
        className={styles.bannerImage}
        eager
      />
      {/* SnGs reuse this tile but their `tableId` is derived from a
          non-numeric tournament name (NaN). Hide the badge unless we
          have a real table number to show. The compact card moves the id
          into the info block (top row), so the banner badge is suppressed. */}
      {!isCompact && Number.isFinite(game.tableId) && game.tableId > 0 && (
        <span className={styles.tableIdBadge}>#{game.tableId}</span>
      )}
      {game.isPrivate && (
        <span className={styles.privateBadge} title='Private table'>
          <LockIcon style={{ fontSize: 12 }} />
        </span>
      )}
    </div>
  );

  // Drop meaningless whole-dollar cents ("$300.00/$600.00" → "$300/$600") so
  // whole-number blinds read cleanly in the lobby tile, while leaving real cent
  // stakes ($0.25/$0.50) untouched. Same rule the compact card already applied.
  const displayStakes = (game.stakes || '').replace(/\.00(?=\D|$)/g, '');

  const body = (
    <div className={styles.body}>
      {/* SnG tournament name/type tag (e.g. "SNG 3P FIFO") — the SnG lobby was
          missing this; cash tiles pass no tournamentName so it's omitted. */}
      {tournamentName ? <span className={styles.tourneyName}>{tournamentName}</span> : null}
      <div className={styles.titleRow}>
        <span className={styles.title}>{displayStakes}</span>
      </div>

      <div
        className={`${styles.buyInRow} ${
          game.buyInMin === game.buyInMax ? styles.buyInRowInline : ''
        }`}>
        <span className={styles.buyInLabel}>BUY-IN</span>
        <span className={styles.cashBuyIn}>
          {game.buyInMin === game.buyInMax ? (
            <>{formatBuyIn(game.buyInMin)}</>
          ) : (
            <>
              {formatBuyIn(game.buyInMin)}&nbsp;-&nbsp;{formatBuyIn(game.buyInMax)}
            </>
          )}
        </span>
      </div>

      <div className={styles.seatsBlock}>
        <SeatDots seated={game.seated} capacity={game.capacity} />
        <span className={styles.seatsNum}>
          {game.seated}/{game.capacity}
        </span>
      </div>
    </div>
  );

  // Compact card (lobby "Card Size" = Compact): a dark, condensed layout per
  // the approved mockup — id → stakes → buy-in → seats stacked on a dark body,
  // with a person-icon seat count instead of the roomy seat-dots. Sit & Go
  // reuses this tile, so the SnG tournament name takes the top-row slot.
  const compactMeta = tournamentName
    ? tournamentName
    : Number.isFinite(game.tableId) && game.tableId > 0
    ? `#${game.tableId}`
    : '';
  const compactBody = (
    <div className={`${styles.body} ${styles.compactBody}`}>
      {compactMeta ? <span className={styles.compactMeta}>{compactMeta}</span> : null}
      <span className={styles.compactStakes}>{displayStakes}</span>
      <div className={styles.compactBuyIn}>
        <span className={styles.compactBuyInLabel}>buy-in</span>
        <span className={styles.compactBuyInVal}>
          {game.buyInMin === game.buyInMax ? (
            <>{formatBuyIn(game.buyInMin)}</>
          ) : (
            <>
              {formatBuyIn(game.buyInMin)}&nbsp;-&nbsp;{formatBuyIn(game.buyInMax)}
            </>
          )}
        </span>
      </div>
      <span className={styles.compactSeats}>
        <PersonIcon className={styles.compactSeatsIcon} aria-hidden />
        {game.seated} / {game.capacity}
      </span>
    </div>
  );

  const bodyContent = isCompact ? compactBody : body;

  // The tile is wrapped in a thin, unclipped layer (.tileWrap) that hosts the
  // active-table glow so it can pulse via opacity instead of animating the
  // tile's box-shadow. See GameTile.module.scss / game-client-performance.md.
  const wrapClass = `${styles.tileWrap} ${reduceAnimations ? styles.reduced : ''} ${
    active ? styles.tileWrapActive : ''
  } ${inView ? '' : styles.paused}`;

  // Full-table waitlist state: an orange wash + CTA strip cover the tile as a
  // "table is full — waitlist available" INDICATOR. Tapping it (overlay or CTA)
  // OPENS the table (observe); it must NOT auto-join the waitlist from the
  // lobby. The player joins the waitlist manually from the in-table Join
  // control (JoinButton) once inside. Rendered as a <div> so the nested buttons
  // stay valid markup.
  if (showWaitlist) {
    const ctaLabel = waitingCount > 0 ? `Join Waitlist - ${waitingCount} in Line` : 'Join Waitlist';
    const onWaitlistTap = () => onClick?.(game.id);
    return (
      <div ref={tileRef} className={wrapClass}>
        <div
          className={`${styles.tile} ${styles.cashTile} ${variantClass} ${
            styles.waitlist
          } ${compactClass} ${inView ? '' : styles.paused}`}>
          {banner}
          {bodyContent}
          <button
            type='button'
            className={styles.overlay}
            aria-label={`Open ${game.stakes} table`}
            onClick={onWaitlistTap}>
            <span className={styles.shimmer} aria-hidden />
          </button>
          <button type='button' className={styles.waitlistCta} onClick={onWaitlistTap}>
            {ctaLabel}
          </button>
        </div>
      </div>
    );
  }

  // Normal tile (and the joined state, which still opens on tap). Per the
  // approved design (mobile-app.jsx: "overlay shows for both join + joined
  // states") the joined tile keeps the orange wash overlay — only the
  // "Join Waitlist" CTA is dropped — plus the pulsing border + "WAITLIST · N"
  // stamp. The overlay here is decorative (pointer-events: none) so the whole
  // tile stays the tap target.
  return (
    <div ref={tileRef} className={wrapClass}>
      {restrictedScope === 'tournament' && !restricted && inView && (
        <PayoutPreload
          id={Number(game.id)}
          revision={`${game.seated}:${game.capacity}:${game.buyInMin}`}
        />
      )}
      <button
        type='button'
        disabled={shutdownBlocked}
        className={`${styles.tile} ${styles.cashTile} ${variantClass} ${compactClass} ${
          active ? styles.active : ''
        } ${joined ? styles.joined : ''} ${restricted ? styles.restrictedTile : ''} ${
          inView ? '' : styles.paused
        }`}
        onClick={handleTileClick}>
        {banner}
        {bodyContent}
        {restricted && <RestrictedOverlay scope={restrictedScope} />}
        {game.pendingShutdown && (
          <span
            className={`${styles.pendingShutdownOverlay} ${
              seatedHere ? styles.pendingShutdownAccessible : ''
            }`}
            aria-label={PENDING_SHUTDOWN_LABEL}>
            {PENDING_SHUTDOWN_LABEL}
          </span>
        )}
        {joined && (
          <>
            <span className={`${styles.overlay} ${styles.overlayStatic}`} aria-hidden>
              <span className={styles.shimmer} aria-hidden />
            </span>
            <span
              className={styles.joinedStamp}
              aria-label={`On waitlist${mySpot?.position ? `, position ${mySpot.position}` : ''}`}>
              {mySpot?.position ? `Waitlist · ${mySpot.position}` : 'Waitlist'}
            </span>
          </>
        )}
      </button>
    </div>
  );
};

// A prod lobby can render 100+ cash tiles, and a single seat-count broadcast
// re-renders the parent — without memo, every tile re-renders even though only
// a handful changed. The comparator compares `game` by value (the broadcast
// hands us fresh objects each time, so reference equality would never hit) plus
// the prop-derived booleans this tile actually renders from: whether the player
// has the table open (drives the .active glow), whether they're seated here
// (gates the Join Waitlist banner), and the waitlistEnabled flag. `onClick` is
// intentionally ignored: the handler only reads fields covered by the
// comparison, so a skipped render can never strand a behaviorally-stale
// callback. `mySpot`/`joined` come from Redux via useSelector, which re-renders
// independently of this memo comparator.
const propsAreEqual = (
  prev: Readonly<CashGameTileProps>,
  next: Readonly<CashGameTileProps>
): boolean =>
  shallowEqualRecord(prev.game, next.game) &&
  prev.waitlistEnabled === next.waitlistEnabled &&
  prev.tournamentName === next.tournamentName &&
  prev.restricted === next.restricted &&
  prev.restrictedScope === next.restrictedScope &&
  prev.size === next.size &&
  (prev.playerGameIds?.has(prev.game.id) ?? false) ===
    (next.playerGameIds?.has(next.game.id) ?? false) &&
  (prev.seatedGameIds?.has(prev.game.id) ?? false) ===
    (next.seatedGameIds?.has(next.game.id) ?? false);

export default memo(CashGameTile, propsAreEqual);
